import React, { useContext, useMemo } from 'react';
import type { RollingChartProps, RollingPeriodEnum } from './types';
import { getAnalysisLabels, findAnyDataExportDisabled } from 'venn-utils';
import AnalysisLineChart from './internal/AnalysisLineChart';
import { AnalysisViewContext } from '../../contexts';
import { PERIODS } from './internal/AnalysisChartLegend';
import { isNil } from 'lodash';
import useCustomRollingWindow from './internal/useCustomRollingWindow';

const AnalysisRollingReturnChart = ({
  frequency,
  analysisGroup,
  subject,
  actualTimeFrame,
  relative,
  noChartErrorComponent,
  noChartTrackingProps,
  downloadMetaData,
  categoryActive,
  exportableGroup,
}: RollingChartProps) => {
  const fullPeriodLabels = useMemo(() => {
    const titleType = getAnalysisLabels(subject.type).main;
    return {
      absolute: `${titleType}'s Full-History Return`,
      relative: `${titleType}'s Full-History Excess Return`,
    };
  }, [subject.type]);

  const { rollingReturnPeriod, onUpdateAnalysisViewParam } = useContext(AnalysisViewContext);
  const { chartDataWithRollingPeriods, customPeriodLoading, setOptimisticLoading } = useCustomRollingWindow(
    subject,
    analysisGroup,
    rollingReturnPeriod,
    relative,
    categoryActive,
    actualTimeFrame,
    'ROLLING_RETURN',
    'rollingReturn',
    fullPeriodLabels,
    noChartTrackingProps?.trackingId,
  );

  const header = relative ? 'Rolling Excess Return' : 'Rolling Return';
  const subheader =
    'Returns are cumulative for rolling periods less than one year and annualized for rolling periods greater than one year.';
  return (
    <AnalysisLineChart
      defaultRollingPeriod={PERIODS.find(({ key }) => key === rollingReturnPeriod.period)}
      rollingPeriodSettings={rollingReturnPeriod}
      loadingSerieData={customPeriodLoading}
      onRollingPeriodChange={(updatedPeriodKey?: RollingPeriodEnum, customPeriodMonths?: number) => {
        if (!isNil(updatedPeriodKey)) {
          onUpdateAnalysisViewParam({ rollingReturnPeriod: { period: updatedPeriodKey } });
          return;
        }
        if (!isNil(customPeriodMonths)) {
          setOptimisticLoading(true);
          onUpdateAnalysisViewParam({ rollingReturnPeriod: { customMonths: customPeriodMonths } });
        }
      }}
      allowCustomWindow
      analysisSubject={subject}
      frequency={frequency}
      relative={relative}
      chartDataWithRollingPeriods={chartDataWithRollingPeriods}
      start={actualTimeFrame.startTime}
      end={actualTimeFrame.endTime}
      header={header}
      subHeader={subheader}
      noChartErrorComponent={noChartErrorComponent}
      noChartTrackingProps={noChartTrackingProps}
      dataExportEvent={{
        description: 'ROLLING_RETURN',
        relativeToBenchmark: relative,
        subjectType: subject.type,
        subjectId: subject.id,
        userUploaded: subject.userUploaded,
      }}
      exportPNGEnabled
      exportXLSXEnabled={!findAnyDataExportDisabled(exportableGroup)}
      yAxisUnitFormat="percent"
      downloadMetaData={downloadMetaData}
    />
  );
};

export default AnalysisRollingReturnChart;
